import React from "react";
import { FormattedMessage } from "react-intl";

import { featureFlags } from "~/common/feature-flags";
import { fetchWithAuth } from "~/common/fetch";
import { AudioEntryRow } from "~/components/patient/patient_shared/audio-entry-row";
import { API_PATIENT_AUDIO } from "~/config";
import type { TPatient } from "~/types/patient";

export function hasPatientSmartPrescription(patient: TPatient): boolean {
  return (
    featureFlags.smartPrescription &&
    (patient.additional_service_tags?.includes("PRESCRIPTION_TRANSCRIBING") ||
      patient.course.payments.extra?.some((service) => service.tag === "PRESCRIPTION_TRANSCRIBING") ||
      Boolean(patient.audio_note))
  );
}

function PatientCardSmartPrescriptionContent({ patient }: { patient: TPatient }) {
  const [audioNote, setAudioNote] = React.useState(patient.audio_note);

  React.useEffect(() => {
    setAudioNote((previousAudioNote) => {
      // Do not overwrite locally fetched ready transcript with stale payload from patient card.
      if (previousAudioNote?.transcript_status === "ready" && previousAudioNote.transcript_text && (!patient.audio_note?.transcript_text || patient.audio_note.transcript_status !== "ready")) {
        return previousAudioNote;
      }
      return patient.audio_note;
    });
  }, [patient.audio_note]);

  React.useEffect(() => {
    const hasUploads = (audioNote?.uploads?.length ?? 0) > 0;
    const hasTranscript = Boolean(audioNote?.transcript_text);
    const status = audioNote?.transcript_status;
    const shouldPoll = status === "processing" || (hasUploads && !hasTranscript && (status == null || status === "empty"));
    if (!audioNote || !shouldPoll) {
      return;
    }

    let isCancelled = false;
    let pollAttempts = 0;
    const maxPollAttempts = 400; // ~10 minutes with 1.5s interval
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollAudioNote = async () => {
      if (isCancelled || pollAttempts >= maxPollAttempts || consecutiveErrors >= maxConsecutiveErrors) {
        return;
      }
      pollAttempts += 1;

      try {
        const response = await fetchWithAuth(API_PATIENT_AUDIO(patient.patient_id), {
          method: "GET",
        });
        if (!response.ok) {
          consecutiveErrors += 1;
        } else {
          consecutiveErrors = 0;
        }
        if (response.ok) {
          const payload = (await response.json()) as { audio_note?: TPatient["audio_note"] | null };
          if (isCancelled) {
            return;
          }
          if (payload.audio_note) {
            setAudioNote(payload.audio_note);
            if (payload.audio_note.transcript_text || payload.audio_note.transcript_status === "ready") {
              return;
            }
            if (payload.audio_note.transcript_status === "empty" && (payload.audio_note.uploads?.length ?? 0) === 0) {
              return;
            }
          }
        }
      } catch {
        consecutiveErrors += 1;
      }

      if (!isCancelled) {
        window.setTimeout(() => {
          void pollAudioNote();
        }, 1500);
      }
    };

    void pollAudioNote();

    return () => {
      isCancelled = true;
    };
    // The full object changes on each poll; keying status fields preserves the 1.5 s interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioNote?.transcript_status, audioNote?.transcript_text, audioNote?.uploads?.length, patient.patient_id]);

  const audioFilesByName = React.useMemo(() => Object.fromEntries((audioNote?.files ?? []).map((file) => [file.file_name, file.url])), [audioNote?.files]);
  const audioEntries = React.useMemo(() => {
    const uploads = audioNote?.uploads ?? [];
    return uploads
      .map((upload, index) => ({
        id: upload.client_id ?? `${upload.uuid ?? "audio"}:${upload.file_name ?? "audio"}:${index}`,
        uploadedAt: upload.uploaded_at ?? 0,
        durationSeconds: upload.duration_seconds ?? null,
        url: (upload.file_name ? audioFilesByName[upload.file_name] : undefined) ?? audioNote?.files?.[index]?.url ?? null,
      }))
      .sort((a, b) => b.uploadedAt - a.uploadedAt);
  }, [audioFilesByName, audioNote?.files, audioNote?.uploads]);
  const [playingAudioId, setPlayingAudioId] = React.useState<string | null>(null);
  const [playbackRates, setPlaybackRates] = React.useState<Record<string, number>>({});
  const audioRefs = React.useRef<Record<string, HTMLAudioElement | null>>({});
  const formatDuration = React.useCallback((durationInSeconds: number) => {
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = durationInSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, []);
  const formatUploadTimestamp = React.useCallback((uploadedAt: number) => {
    if (!uploadedAt) {
      return "";
    }

    const uploadedDate = new Date(uploadedAt);
    const now = new Date();
    const isToday = uploadedDate.getFullYear() === now.getFullYear() && uploadedDate.getMonth() === now.getMonth() && uploadedDate.getDate() === now.getDate();

    if (isToday) {
      return `Сегодня, ${uploadedDate.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    }

    return uploadedDate.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);
  const setPlaybackRate = React.useCallback((audioId: string, rate: number) => {
    setPlaybackRates((previous) => {
      const audio = audioRefs.current[audioId];
      if (audio) audio.playbackRate = rate;
      return { ...previous, [audioId]: rate };
    });
  }, []);
  const toggleAudioPlayback = React.useCallback(
    async (audioId: string) => {
      const audio = audioRefs.current[audioId];
      if (!audio) {
        return;
      }

      if (playingAudioId === audioId) {
        audio.pause();
        audio.currentTime = 0;
        setPlayingAudioId(null);
        return;
      }

      if (playingAudioId && audioRefs.current[playingAudioId]) {
        const previousAudio = audioRefs.current[playingAudioId];
        previousAudio.pause();
        previousAudio.currentTime = 0;
      }

      try {
        audio.playbackRate = playbackRates[audioId] ?? 1;
        await audio.play();
        setPlayingAudioId(audioId);
      } catch (error) {
        console.error(error);
      }
    },
    [playbackRates, playingAudioId],
  );

  return (
    <div className="tw-mt-3">
      <h4 className="block">
        <FormattedMessage id="additional.services.smart.audio.records_title" />
      </h4>
      {audioEntries.length > 0 ? (
        <div className="tw-flex tw-flex-col tw-gap-2">
          {audioEntries.map((entry, index) => {
            const durationText = typeof entry.durationSeconds == "number" ? formatDuration(Math.max(0, entry.durationSeconds)) : "--:--";
            const isPlaying = playingAudioId === entry.id;
            const playbackRate = playbackRates[entry.id] ?? 1;

            return (
              <AudioEntryRow
                key={entry.id}
                number={audioEntries.length - index}
                sourceUrl={entry.url}
                durationText={durationText}
                timestampText={formatUploadTimestamp(entry.uploadedAt)}
                isPlaying={isPlaying}
                playbackRate={playbackRate}
                onTogglePlayback={() => void toggleAudioPlayback(entry.id)}
                onPlaybackRateChange={(rate) => setPlaybackRate(entry.id, rate)}
                setAudioRef={(node) => {
                  audioRefs.current[entry.id] = node;
                }}
                onEnded={() => {
                  if (playingAudioId === entry.id) setPlayingAudioId(null);
                }}
              />
            );
          })}
        </div>
      ) : (
        <p>
          <FormattedMessage id="None" />
        </p>
      )}
    </div>
  );
}

export function PatientCardSmartPrescription({ patient }: { patient: TPatient }) {
  if (!hasPatientSmartPrescription(patient)) {
    return null;
  }

  return <PatientCardSmartPrescriptionContent patient={patient} />;
}
